/** Inline subscribe form — рендер в блоке страницы (mode=inline). Trigger всегда manual. */
import { useState, useEffect, useRef } from 'preact/hooks';
import { t, resolveLocalizedString } from '../i18n';
import { getConfig } from '../config';
import { subscribeLeads, subscribeEvents } from '../api';
import { store } from '../store';
import { themeTokens } from '../themeTokens';
import { getSessionId } from '../trackingPipeline';
import { recordSubmit } from '../subscribeGuards';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateEmail(v: string): boolean {
  return EMAIL_RE.test(v.trim());
}

interface SubscribeInlineFormProps {
  settings?: { subscribe?: { title?: string | Record<string, string>; subtitle?: string | Record<string, string>; offer_text?: string | Record<string, string>; cta_text?: string | Record<string, string>; fields?: { email?: boolean; phone?: boolean }; consent_required?: boolean } };
}

export function SubscribeInlineForm({ settings }: SubscribeInlineFormProps) {
  const config = getConfig();
  const sub = settings?.subscribe ?? {};
  const title = resolveLocalizedString(sub.title as string | Record<string, string> | undefined, t('subscribe.title'));
  const subtitle = resolveLocalizedString(sub.subtitle as string | Record<string, string> | undefined, t('subscribe.subtitle'));
  const offerText = resolveLocalizedString(sub.offer_text as string | Record<string, string> | undefined, '');
  const ctaText = resolveLocalizedString(sub.cta_text as string | Record<string, string> | undefined, t('subscribe.cta'));
  const showPhone = sub.fields?.phone ?? false;
  const consentRequired = sub.consent_required !== false;

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const submissionPendingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const shownSentRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || shownSentRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !shownSentRef.current) {
          shownSentRef.current = true;
          subscribeEvents(config, 'subscribe_shown', {
            trigger_type: 'manual',
            source_page: window.location.pathname || '/',
            session_id: getSessionId(),
          });
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (submissionPendingRef.current) return;
    submissionPendingRef.current = true;
    let submissionSucceeded = false;
    try {
      setError('');
      if (!validateEmail(email)) {
        setError(t('subscribe.invalidEmail'));
        return;
      }
      if (consentRequired && !consent) {
        setError(t('subscribe.consentRequired'));
        return;
      }
      setLoading(true);
      const auth = store.getAuth();
      const res = await subscribeLeads(config, {
        email: email.trim(),
        phone: showPhone ? phone.trim() || undefined : undefined,
        trigger_type: 'manual',
        source_page: typeof window !== 'undefined' ? window.location.pathname || '/' : '/',
        consent: consentRequired ? consent : true,
        customer_login: auth?.login,
        is_authenticated: !!auth?.login,
        session_id: getSessionId(),
        utm: (() => {
          if (typeof window === 'undefined') return {};
          const s = new URLSearchParams(window.location.search);
          const u: Record<string, string> = {};
          const v = s.get('utm_source'); if (v) u.source = v;
          const m = s.get('utm_medium'); if (m) u.medium = m;
          const c = s.get('utm_campaign'); if (c) u.campaign = c;
          return u;
        })(),
      });
      if (res.status === 200 && !res.error && (res.result as any)?.ok) {
        submissionSucceeded = true;
        setSuccess(true);
        recordSubmit(config.siteKey);
        store.addToast(t('subscribe.success'), 'success');
      } else {
        setError(res.error || t('api.genericError'));
      }
    } catch {
      setError(t('api.networkError'));
    } finally {
      submissionPendingRef.current = submissionSucceeded;
      setLoading(false);
    }
  };

  return (
    <div ref={containerRef} class="sw-subscribe-inline" style={{ borderRadius: themeTokens.radiusModal }}>
      <div class="border rounded p-4" style={{ background: 'var(--sw-bg,#fff)', borderColor: 'var(--sw-border,#dee2e6)' }}>
        <h5 class="fw-bold mb-3">{title}</h5>
        {success ? (
          <div class="text-center text-success py-2">{t('subscribe.success')}</div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {subtitle && <p class="text-muted small mb-3">{subtitle}</p>}
            {offerText && <p class="small mb-3">{offerText}</p>}
            <div class="mb-3">
              <label class="form-label">{t('subscribe.emailLabel')}</label>
              <input
                type="email"
                class="form-control"
                placeholder={t('subscribe.emailPlaceholder')}
                value={email}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                disabled={loading}
                required
              />
            </div>
            {showPhone && (
              <div class="mb-3">
                <label class="form-label">{t('subscribe.phoneLabel')}</label>
                <input
                  type="tel"
                  class="form-control"
                  placeholder={t('subscribe.phonePlaceholder')}
                  value={phone}
                  onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
                  disabled={loading}
                />
              </div>
            )}
            {consentRequired && (
              <div class="form-check mb-3">
                <input class="form-check-input" type="checkbox" id="subscribe-inline-consent" checked={consent} onChange={(e) => setConsent((e.target as HTMLInputElement).checked)} />
                <label class="form-check-label small" htmlFor="subscribe-inline-consent">{t('subscribe.consentLabel')}</label>
              </div>
            )}
            {error && <div class="alert alert-danger py-2 small mb-3">{error}</div>}
            <button type="submit" class="btn btn-primary w-100" disabled={loading}>
              {loading ? <span class="spinner-border spinner-border-sm me-2"></span> : null}
              {ctaText}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
